import { SuppliersCodes } from '@luxuryescapes/contract-svc-bedbank/constants/suppliers';

import { OFFER_TYPE_HOTEL } from '~/consts/offerTypes';
import { ROLE_ADMIN_USER } from '~/consts/roles';

/**
 * A loyalty package upgrade gives an eligible member a better board type at the price of the
 * cheaper rate they selected, with the difference absorbed by the business. svc-order records
 * the pre-upgrade rate and what it cost; nothing else in admin surfaces either.
 */

/** Board codes in upgrade order, matching svc-accommodation's `BOARD_UPGRADE_ORDER`. */
const BOARD_LABELS: Record<string, string> = {
  RO: 'Room only',
  BB: 'Bed & breakfast',
  HB: 'Half board',
  FB: 'Full board',
  AI: 'All inclusive',
};

/** `SuppliersCodes` maps name → code; upgrade rate ids carry the code, so invert it. */
const SUPPLIER_BY_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(SuppliersCodes).map(([supplier, code]) => [code, supplier]),
);

export interface LoyaltyUpgradeRate {
  rateId: string;
  /** Absent when the rate id is not the bedbank composite form. */
  boardCode?: string;
  boardLabel?: string;
  supplierCode?: string;
  supplierName?: string;
}

export interface LoyaltyUpgradeView {
  source: App.OrderItem['loyalty_upgrade_source'];
  /** Flash records its pre-upgrade package here instead of in `source`. */
  basePackageId?: string | null;
  /** Target minus source, AUD. Null until svc-order's cost job resolves both sides. */
  cost?: number | null;
  upgradeType?: 'room' | 'package' | null;
  sourceRate?: LoyaltyUpgradeRate;
  bookedRate?: LoyaltyUpgradeRate;
  /** Bedbank only — the reservation the booked rate resolves to. */
  bookedReservationId?: string | null;
  /** Flash and LPC — the offer both packages belong to, so their names can be resolved together. */
  offerId?: string | null;
  /** Flash and LPC — the booked package, the counterpart to `basePackageId`. */
  bookedPackageId?: string | null;
  isCancelled: boolean;
}

/** A package named from the offer, for either side of a flash upgrade. */
export interface UpgradePackage {
  id: string;
  name?: string;
  roomTypeName?: string;
}

/**
 * Finds a package within its offer.
 *
 * Which id the upgrade recorded varies — `loyalty_base_package_id` is a Salesforce id on the flash
 * bookings seen so far, while other paths carry the LE or numeric id — so match across all three
 * rather than assume one.
 */
export function findOfferPackage(
  packages: Array<App.AccommodationPackage> | undefined,
  packageId: string | null | undefined,
): UpgradePackage | undefined {
  if (!packages || !packageId) return undefined;

  const match = packages.find(
    (pkg) =>
      pkg.id_salesforce_external === packageId || pkg.le_package_id === packageId || String(pkg.id ?? '') === packageId,
  );
  if (!match) return undefined;

  return { id: packageId, name: match.name, roomTypeName: match.room_type?.name };
}

/**
 * Names the upgrade after what was actually booked, so the heading matches the item beneath it.
 * The type is absent on older items and on any vertical that never set it, hence the neutral
 * fallback rather than naming a kind of upgrade we cannot confirm.
 */
export function getUpgradeTitle(upgradeType: LoyaltyUpgradeView['upgradeType']): string {
  if (upgradeType === 'package') return 'Hotel Package Upgrade';
  if (upgradeType === 'room') return 'Hotel Room Upgrade';
  return 'Hotel Upgrade';
}

/**
 * The benefit payment the checkout records against a loyalty package upgrade. Its amount is the
 * sell-price gap the member did not pay, in the order currency — not the business cost in AUD.
 */
export const HOTEL_UPGRADE_PAYMENT_TYPE = 'le_points_hotel_upgrade';

/** Which table the booked item lives in — svc-order's own `targetItemType`. */
export type UpgradeItemType = 'accommodation' | 'bedbank';

/**
 * The service that priced the booked rate, mirroring the branches of svc-order's
 * `resolveTargetCostAud`: a bedbank target reads `cost_price_inclusive`, a flash target the offer
 * package cost, an LPC target the reservation room-rate cost.
 *
 * Derived, not recorded — svc-order persists a `service_type` for the upgrade *source* only. The
 * vertical is exact, since it is the same table split svc-order branches on. The flash/LPC split is
 * second-hand: svc-order keys off `reservation.offer_type`, which no presenter exposes, so this
 * reads the offer's own type instead. The two are set from the same offer at booking and should
 * agree, but nothing here can confirm that.
 */
export function getBookedServiceType(
  itemType: UpgradeItemType | undefined,
  offerType: string | undefined,
): 'bedbank' | 'flash' | 'lpc' | undefined {
  if (itemType === 'bedbank') return 'bedbank';
  if (itemType !== 'accommodation' || !offerType) return undefined;
  return offerType === OFFER_TYPE_HOTEL ? 'flash' : 'lpc';
}

const SERVICE_TYPE_LABELS: Record<string, string> = {
  bedbank: 'Bedbank',
  flash: 'Flash',
  lpc: 'LPC',
};

/** Presentable form of a service type; unknown values pass through as recorded. */
export function formatServiceType(serviceType: string | null | undefined): string | undefined {
  if (!serviceType) return undefined;
  return SERVICE_TYPE_LABELS[serviceType] ?? serviceType;
}

/** Only `admin-user` sees upgrade details — `cost` is cost data, restricted by svc-order too. */
export function canViewLoyaltyUpgradeDetails(user: Pick<App.User, 'roles'> | null | undefined): boolean {
  return !!user?.roles.includes(ROLE_ADMIN_USER);
}

/**
 * Bedbank rate ids are `<supplierCode>|<roomId>|<boardCode>|…`, so board and supplier come free.
 * LPC rate ids are reservation room-rate UUIDs and yield neither.
 */
export function parseUpgradeRate(rateId: string | null | undefined): LoyaltyUpgradeRate | undefined {
  if (!rateId) return undefined;

  const [supplierCode, , boardCode] = rateId.split('|');
  const boardLabel = boardCode ? BOARD_LABELS[boardCode] : undefined;
  if (!boardLabel) return { rateId };

  return {
    rateId,
    boardCode,
    boardLabel,
    supplierCode,
    supplierName: SUPPLIER_BY_CODE[supplierCode],
  };
}

type UpgradeableItem = Partial<
  Pick<
    App.OrderItem,
    | 'loyalty_upgrade_source'
    | 'loyalty_base_package_id'
    | 'loyalty_package_upgrade_cost'
    | 'loyalty_package_upgrade_type'
    | 'status'
  >
> & {
  /** Bedbank only — the booked (post-upgrade) rate. */
  fk_room_rate_id?: string;
  /** Accommodation only — the offer the booked and base packages both belong to. */
  fk_offer?: string;
  /** Accommodation only — the booked package. */
  fk_package?: string;
  /**
   * Bedbank only — the reservation the upgrade was booked into. The booked side has no session
   * id: svc-order consumes the bedbank session to create the reservation and never stores it, so
   * this is the handle that actually resolves to something.
   */
  id_reservation?: string;
};

/**
 * The upgrade recorded against an item, or nothing if it was not upgraded.
 *
 * Any one marker is enough. A flash upgrade has only `loyalty_base_package_id`; an LPC or bedbank
 * upgrade has `loyalty_upgrade_source`; and an item whose cost resolved but whose other fields are
 * somehow absent still deserves to be shown rather than silently hidden.
 */
export function getLoyaltyUpgrade(item: UpgradeableItem | null | undefined): LoyaltyUpgradeView | undefined {
  if (!item) return undefined;

  const source = item.loyalty_upgrade_source ?? null;
  const basePackageId = item.loyalty_base_package_id ?? null;
  const cost = item.loyalty_package_upgrade_cost ?? null;
  if (!source && !basePackageId && cost === null) return undefined;

  return {
    source,
    basePackageId,
    cost,
    upgradeType: item.loyalty_package_upgrade_type ?? null,
    sourceRate: parseUpgradeRate(source?.room_rate_id),
    bookedRate: parseUpgradeRate(item.fk_room_rate_id),
    bookedReservationId: item.id_reservation ?? null,
    offerId: item.fk_offer ?? null,
    bookedPackageId: item.fk_package ?? null,
    isCancelled: item.status === 'cancelled',
  };
}
